import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, User, Mail, Phone, MapPin, Calendar, Clock, DollarSign, Download, Check } from 'lucide-react';
import { getReceiptByBookingNumber, downloadReceiptPdf } from '../utils/api';
import { formatTime12Hour } from '../utils/timeFormat';

const ReceiptPage = () => {
  const { bookingNumber } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingNumber]);

  const fetchReceipt = async () => {
    try {
      setLoading(true);
      const data = await getReceiptByBookingNumber(bookingNumber);
      setBooking(data);
    } catch (err) {
      console.error('Error fetching receipt:', err);
      setError('Receipt not found');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    downloadReceiptPdf(bookingNumber);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading receipt...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <p className="text-red-600 text-lg">{error || 'Receipt not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const finalFare = booking.finalFare || booking.estimatedFare || 0;
  const receiptNumber = booking.receiptNumber || `RCP-${booking.id}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-8 rounded-t-2xl text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8" />
              <h1 className="text-3xl font-bold">TAXI-98</h1>
            </div>
            {booking.receiptStatus === 'sent' && (
              <div className="bg-green-500 px-4 py-2 rounded-full flex items-center space-x-2">
                <Check className="w-5 h-5" />
                <span className="font-semibold">Receipt Sent</span>
              </div>
            )}
          </div>
          <p className="text-cyan-100">Nassau, Bahamas</p>
          <p className="text-cyan-100">242-810-7335 • stephen@taxi-98.com</p>
          <p className="text-cyan-100">www.taxi-98.com</p>
        </div>

        {/* Receipt Details */}
        <div className="bg-white shadow-xl rounded-b-2xl">
          {/* Receipt Title */}
          <div className="border-b border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">TRIP RECEIPT</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Receipt Number</p>
                <p className="font-bold text-gray-900">{receiptNumber}</p>
              </div>
              <div>
                <p className="text-gray-500">Booking Number</p>
                <p className="font-bold text-gray-900">{booking.id}</p>
              </div>
              <div>
                <p className="text-gray-500">Receipt Date</p>
                <p className="font-bold text-gray-900">
                  {booking.receiptGeneratedAt 
                    ? new Date(booking.receiptGeneratedAt).toLocaleDateString()
                    : new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="border-b border-gray-200 p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 text-cyan-600 mr-2" />
              Customer Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <span className="text-gray-500 w-32">Name:</span>
                <span className="font-semibold text-gray-900">{booking.passengerName}</span>
              </div>
              <div className="flex items-start">
                <Mail className="w-4 h-4 text-gray-400 mt-0.5 mr-2" />
                <span className="text-gray-500 w-28">Email:</span>
                <span className="text-gray-900">{booking.passengerEmail}</span>
              </div>
              <div className="flex items-start">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5 mr-2" />
                <span className="text-gray-500 w-28">Phone:</span>
                <span className="text-gray-900">{booking.passengerPhone}</span>
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="border-b border-gray-200 p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 text-cyan-600 mr-2" />
              Trip Details
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Pickup Location</p>
                  <p className="font-semibold text-gray-900">{booking.pickupLocation}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Dropoff Location</p>
                  <p className="font-semibold text-gray-900">{booking.dropoffLocation}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Trip Date</p>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4 text-cyan-600" />
                    <p className="font-semibold text-gray-900">{booking.date}</p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Pickup Time</p>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-cyan-600" />
                    <p className="font-semibold text-gray-900">{formatTime12Hour(booking.time)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Driver</p>
                  <p className="font-semibold text-gray-900">{booking.driverName || 'Stephen'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Passengers</p>
                  <p className="font-semibold text-gray-900">{booking.passengers}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Suitcases</p>
                  <p className="font-semibold text-gray-900">{booking.luggage || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Oversized Items</p>
                  <p className="font-semibold text-gray-900">{booking.largeBags || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Payment Method</p>
                  <p className="font-semibold text-gray-900">{booking.paymentMethod}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Fare */}
          <div className="border-b border-gray-200 p-8 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Fare Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-gray-700">
                <span>Base Fare</span>
                <span>${finalFare.toFixed(2)} BSD</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t-2 border-cyan-500">
                <span>TOTAL FARE</span>
                <span className="text-cyan-600">${finalFare.toFixed(2)} BSD</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          {booking.completedAt && (
            <div className="border-b border-gray-200 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Trip Timeline</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Completed At:</span>
                  <span className="text-gray-900 font-semibold">
                    {new Date(booking.completedAt).toLocaleString()}
                  </span>
                </div>
                {booking.receiptGeneratedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Receipt Generated:</span>
                    <span className="text-gray-900 font-semibold">
                      {new Date(booking.receiptGeneratedAt).toLocaleString()}
                    </span>
                  </div>
                )}
                {booking.receiptSentAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Receipt Sent:</span>
                    <span className="text-gray-900 font-semibold">
                      {new Date(booking.receiptSentAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-8 text-center">
            <p className="text-gray-900 font-semibold mb-2">Thank you for choosing Taxi-98!</p>
            <p className="text-gray-600 mb-6">
              We appreciate your business and look forward to serving you again.
            </p>
            
            {booking.receiptPdfPath && (
              <button
                onClick={handleDownload}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-3 rounded-lg font-bold hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 inline-flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Download PDF Receipt</span>
              </button>
            )}
            
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-gray-500 text-sm">
                Taxi-98 • Nassau, Bahamas • www.taxi-98.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPage;
